import type { KeyboardEvent, ReactNode } from 'react';
import { useId, useState } from 'react';
import {
  KeyboardArrowDown as CollapsedIcon,
  KeyboardArrowUp as ExpandedIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  ButtonBase,
  Chip,
  Stack,
  Typography,
} from '@mui/material';

import type { WaitingRoomEntry } from '../types';
import {
  formatWaitingRoomWait,
  getWaitingRoomDarkChipSx,
  getWaitingRoomInitials,
  getWaitingRoomWaitColor,
} from '../utils';

interface WaitingRoomEntryRowProps {
  entry: WaitingRoomEntry;
  variant?: 'light' | 'dark';
  density?: 'compact' | 'regular';
  action?: ReactNode;
  expandedContent?: ReactNode;
  defaultExpanded?: boolean;
  onClick?: () => void;
}

export function WaitingRoomEntryRow({
  entry,
  variant = 'light',
  density = 'regular',
  action,
  expandedContent,
  defaultExpanded = false,
  onClick,
}: WaitingRoomEntryRowProps) {
  const expandedContentId = useId();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const isDark = variant === 'dark';
  const isCompact = density === 'compact';
  const isExpandable = Boolean(expandedContent);
  const patientName = entry.patient_name || 'Unnamed patient';
  const waitColor = getWaitingRoomWaitColor(entry.wait_seconds);

  const handleToggle = () => {
    if (isExpandable) {
      setIsExpanded(current => !current);
      return;
    }
    onClick?.();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  const content = (
    <>
      <Avatar
        sx={{
          width: isCompact ? 32 : 40,
          height: isCompact ? 32 : 40,
          fontSize: isCompact ? 12 : 14,
          fontWeight: 700,
          bgcolor: isDark ? 'grey.700' : 'grey.200',
          color: isDark ? 'common.white' : 'grey.700',
          flexShrink: 0,
        }}
      >
        {getWaitingRoomInitials(patientName)}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack spacing={0.5} sx={{ alignItems: 'flex-start', minWidth: 0 }}>
          <Typography
            variant={isCompact ? 'body2' : 'subtitle2'}
            noWrap
            sx={{ color: isDark ? 'common.white' : 'text.primary', fontWeight: 700, minWidth: 0 }}
          >
            {patientName}
          </Typography>
          <Chip
            label={formatWaitingRoomWait(entry.wait_seconds)}
            color={waitColor}
            variant="soft"
            size="xSmall"
            sx={isDark ? getWaitingRoomDarkChipSx(waitColor) : undefined}
          />
        </Stack>
      </Box>

      {action ?? (
        isExpandable ? (
          isExpanded ? (
            <ExpandedIcon
              sx={{ fontSize: 20, color: isDark ? 'grey.300' : 'text.secondary', flexShrink: 0 }}
            />
          ) : (
            <CollapsedIcon
              sx={{ fontSize: 20, color: isDark ? 'grey.300' : 'text.secondary', flexShrink: 0 }}
            />
          )
        ) : (
          <CollapsedIcon
            sx={{ fontSize: 20, color: isDark ? 'grey.500' : 'text.disabled', flexShrink: 0 }}
          />
        )
      )}
    </>
  );

  const rowSx = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    textAlign: 'left',
    px: isCompact ? 1 : 1.25,
    py: isCompact ? 1 : 1.25,
    borderRadius: 2,
    bgcolor: isDark ? 'grey.800' : 'background.paper',
    border: '1px solid',
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'divider',
    transition: 'background-color 160ms ease, border-color 160ms ease',
    '&:hover': onClick || isExpandable
      ? {
          bgcolor: isDark ? 'grey.700' : 'action.hover',
          borderColor: isDark ? 'rgba(255,255,255,0.14)' : 'action.selected',
        }
      : undefined,
  } as const;

  if (isExpandable) {
    return (
      <Box sx={{ ...rowSx, flexDirection: 'column', alignItems: 'stretch', gap: 0 }}>
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-controls={expandedContentId}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            outline: 0,
            cursor: 'pointer',
          }}
        >
          {content}
        </Box>

        {isExpanded && (
          <Box
            id={expandedContentId}
            sx={{
              pt: 1,
              mt: 1,
              borderTop: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
            }}
          >
            {expandedContent}
          </Box>
        )}
      </Box>
    );
  }

  if (onClick) {
    return (
      <ButtonBase onClick={onClick} sx={rowSx}>
        {content}
      </ButtonBase>
    );
  }

  return <Box sx={rowSx}>{content}</Box>;
}
